// src/components/reporting/AnalyticsPage.js - Main analytics reports list view
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../contexts/AuthContext';
import { useRoles } from '../../hooks/useRoles';
import { useApiCall } from '../../hooks/useApiCall';
import apiService from '../../services/apiService';
import RunReportDialog from './RunReportDialog';
import ReportDetailPage from './ReportDetailPage';
import '../../styles/globals.css';
import './Analytics.css';

const AnalyticsPage = ({ tenant, onBack, onViewReport }) => {
  const { user } = useAuth();
  const { userProfile } = useRoles();
  const [reports, setReports] = useState([]);
  const [showRunDialog, setShowRunDialog] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0
  });
  
  const { loading, error, makeRequest } = useApiCall();

  useEffect(() => {
    if (userProfile) {
      loadReports();
    }
  }, [pagination.page, userProfile, tenant]);

  const loadReports = async () => {
    if (!userProfile) {
      // console.log('Waiting for userProfile to load...');
      return;
    }

    try {
      const data = await makeRequest(
        () => apiService.get(
          `/api/v1/analytics/reports?page=${pagination.page}&limit=${pagination.limit}&sortBy=created_at&sortOrder=DESC`,
          user
        ),
        'Loading reports...'
      );
      
      if (data.success && data.data) {
        // console.log('Raw reports from API:', data.data.reports);
        // console.log('Current userProfile.id:', userProfile.id);
        // console.log('Current tenant:', tenant);
        
        // Filter reports by current user and optionally by tenant
        const userReports = data.data.reports.filter(report => {
          // Must be created by current user
          if (!report.createdBy) {
            console.warn('Report missing createdBy:', report);
            return false;
          }
          
          if (report.createdBy.id !== userProfile.id) {
            // console.log(`Filtering out report ${report.id} - created by user ${report.createdBy.id}, current user is ${userProfile.id}`);
            return false;
          }
          
          // If tenant context is provided, filter by tenant
          // Note: Only filter by tenant if the report has a tenantId
          if (tenant && report.tenantId) {
            if (report.tenantId !== tenant.id) {
              // console.log(`Filtering out report ${report.id} - wrong tenant (${report.tenantId} vs ${tenant.id})`);
              return false;
            }
          }
          
          return true;
        });
        
        // console.log('Filtered to user reports:', userReports);
        setReports(userReports);
        setPagination(prev => ({
          ...prev,
          total: data.data.pagination?.total || userReports.length,
          totalPages: data.data.pagination?.totalPages || 1
        }));
      }
    } catch (err) {
      console.error('Error loading reports:', err);
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this report?')) {
      return;
    }

    try {
      await makeRequest(
        () => apiService.delete(`/api/v1/analytics/reports/${reportId}`, user),
        'Deleting report...'
      );
      
      // Reload reports after deletion
      await loadReports();
    } catch (err) {
      console.error('Error deleting report:', err);
    }
  };

  const handleReportCreated = (reportId) => {
    setShowRunDialog(false);
    if (reportId) {
      if (onViewReport) {
        // Navigate via ReportingDashboard to keep back-navigation consistent
        onViewReport(reportId);
      } else {
        // Fallback: open inline within AnalyticsPage
        setSelectedReport(reportId);
      }
    } else {
      loadReports();
    }
  };

  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusBadgeClass = (status) => {
    switch(status) {
      case 'completed': return 'status-badge status-completed';
      case 'processing': return 'status-badge status-processing';
      case 'failed': return 'status-badge status-failed';
      default: return 'status-badge';
    }
  };

  const getTemplateBadgeClass = (reportType) => {
    switch(reportType) {
      case 'customer_intelligence': return 'template-badge template-customer-intelligence';
      case 'communication_skills': return 'template-badge template-communication-skills';
      default: return 'template-badge';
    }
  };

  const getTemplateLabel = (reportType) => {
    switch(reportType) {
      case 'customer_intelligence': return 'Customer Intelligence';
      case 'communication_skills': return 'Communication Skills';
      case 'ad_hoc': return 'Ad Hoc Analysis';
      default: return reportType;
    }
  };

  // If viewing a specific report, show detail page
  if (selectedReport) {
    return (
      <ReportDetailPage
        reportId={selectedReport}
        onBack={() => setSelectedReport(null)}
      />
    );
  }

  // Wait for userProfile to load
  if (!userProfile) {
    return (
      <div className="analytics-page">
        <div className="text-center py-12">
          <div className="loading-dots mx-auto mb-3">
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
          </div>
          <p className="text-gray-600">Loading user profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-page">
      {/* Header */}
      <div className="analytics-header">
        <div className="analytics-header-left">
          {onBack && (
            <button
              onClick={onBack}
              className="btn btn-secondary mr-3"
              title="Back to Reporting Dashboard"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
          )}
          <div>
            <h2 className="analytics-title">Analytics Reports</h2>
            {tenant && (
              <p className="text-sm text-gray-600 mt-1">
                Viewing reports for <span className="font-semibold">{tenant.name}</span>
              </p>
            )}
          </div>
        </div>
        {/* <button
          onClick={() => setShowRunDialog(true)}
          className="btn btn-primary"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Run New Report
        </button> */}
      </div>

      {/* Reports List */}
      <div className="reports-container">
        {loading && reports.length === 0 ? (
          <div className="text-center py-12">
            <div className="loading-dots mx-auto mb-3">
              <div className="loading-dot"></div>
              <div className="loading-dot"></div>
              <div className="loading-dot"></div>
            </div>
            <p className="text-gray-600">Loading reports...</p>
          </div>
        ) : reports.length === 0 ? (
          <div className="empty-state">
            <svg className="empty-state-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <h3 className="empty-state-title">No reports yet</h3>
            <p className="empty-state-text">Click &quot;Run New Report&quot; to get started</p>
          </div>
        ) : (
          <div className="reports-list">
            {reports.map(report => (
              <div key={report.id} className="report-item">
                <div className="report-item-content">
                  <div className="report-item-header">
                    <button
                      onClick={() => setSelectedReport(report.id)}
                      className="report-title-link"
                    >
                      {report.title}
                    </button>
                    <div className="report-badges">
                      <span className={getTemplateBadgeClass(report.reportType)}>
                        {getTemplateLabel(report.reportType)}
                      </span>
                      <span className={getStatusBadgeClass(report.status)}>
                        {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
                      </span>
                    </div>
                  </div>
                  <div className="report-item-meta">
                    <span className="report-date">{formatDateTime(report.createdAt)}</span>
                    {report.scope?.conversationCount && (
                      <span className="report-conversations">
                        {report.scope.conversationCount} conversation{report.scope.conversationCount !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteReport(report.id)}
                  className="report-delete-btn"
                  title="Delete report"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={pagination.page === 1}
              className="btn btn-secondary btn-sm"
            >
              Previous
            </button>
            <span className="pagination-info">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={pagination.page >= pagination.totalPages}
              className="btn btn-secondary btn-sm"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Run Report Dialog */}
      {showRunDialog && (
        <RunReportDialog
          tenant={tenant}
          onClose={() => setShowRunDialog(false)}
          onReportCreated={handleReportCreated}
        />
      )}
    </div>
  );
};

AnalyticsPage.propTypes = {
  tenant: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    slug: PropTypes.string
  }),
  onBack: PropTypes.func,
  onViewReport: PropTypes.func
};

export default AnalyticsPage;